import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import calculateWeekNumber from "../../../Helpers/weekNumber";
import cloneDeep from "lodash/cloneDeep";
import { Projects } from "../../../services/projectsService";
import FormField from "../../components/FormField";
import SearchSelect from "../../components/SearchSelect";
import SegmentedControl from "../../components/SegmentedControl";
import TextInput from "../../components/TextInput";
import Toggle from "../../components/Toggle";
import { FileKey } from "lucide-react";

const firstError = (errors, key) => {
  const value = errors?.[key];
  return Array.isArray(value) ? value[0] : value;
};

const Location = (props) => {
  const { handleInputChange } = props;
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const { user: authUser } = useSelector((state) => state.auth);
  const locationData = useMemo(() => props?.values?.location || {}, [props?.values?.location]);
  const possessionData = useMemo(() => props?.values?.possessions || [], [props?.values?.possessions]);
  const projectOptions = projects.map((project) => ({
    label: [project.number, project.name].filter(Boolean).join(" - ") || "Unnamed project",
    value: project.id || project._id || project.number,
    project,
  }));
  const selectedProjectId = locationData.project?.id || locationData.project?._id || locationData.project?.number || locationData.project;
  const selectedProject = projectOptions.find((option) => String(option.value) === String(selectedProjectId)) || null;

  useEffect(() => {
    if (authUser?.organisation?.swp_org_id) {
      Projects.getProjects(authUser.organisation.swp_org_id)
        .then((response) => {
          setProjects(response);
          setProjectsLoading(false);
        })
        .catch(() => setProjectsLoading(false));
    } else {
      setProjectsLoading(false);
    }
  }, [authUser?.organisation?.swp_org_id]);

  useEffect(() => {
    if (possessionData?.length > 0 && possessionData?.[0]?.planned_start_date && possessionData?.[possessionData.length - 1]?.planned_end_date) {
      const plannedStartDate = possessionData[0].planned_start_date;
      const plannedEndDate = possessionData[possessionData.length - 1].planned_end_date;
      const weekNumber = calculateWeekNumber(plannedStartDate);
      if (locationData.planned_start_date === plannedStartDate &&
          locationData.planned_end_date === plannedEndDate &&
          locationData.week_no === weekNumber) return;
      const updatedLocationData = {
        ...cloneDeep(locationData),
        week_no: weekNumber,
        planned_start_date: plannedStartDate,
        planned_end_date: plannedEndDate,
      };
      handleInputChange("location", updatedLocationData, { systemSync: true });
    }
  }, [handleInputChange, locationData, possessionData]);

  return (
    <section className="wsm-pack-form-section">
      <div className="wsm-pack-form-section__heading">
        <div className="wsm-pack-form-section__heading-title">
          <span className="wsm-pack-form-section__heading-icon"><FileKey aria-hidden="true" /></span>
          <div>
          <h3>Pack identification</h3>
          <p>Use the references shown on the possession plan.</p>
          </div>
        </div>
      </div>

      <SearchSelect
        helpText="Choose the related project where applicable."
        id="location--project"
        label="Project"
        loading={projectsLoading}
        onChange={(option) => props.handleInputChange("location.project", option?.project || "")}
        optional
        options={projectOptions}
        value={selectedProject}
      />

      <div className="wsm-pack-form__identification-grid">
        <FormField
          error={firstError(props.errors, "data.location.won_item_no")}
          id="location--won_item_no"
          label="WON item number"
          required
        >
          {(fieldProps) => (
            <TextInput
              {...fieldProps}
              name="won_item_no"
              onChange={(event) => props.handleInputChange("location.won_item_no", event.target.value)}
              value={locationData.won_item_no || ""}
            />
          )}
        </FormField>
        <FormField
          error={firstError(props.errors, "data.location.possession_no")}
          helpText="For example, P2026/184920"
          id="location--possession_no"
          label="Possession number"
          required
        >
          {(fieldProps) => (
            <TextInput
              {...fieldProps}
              name="possession_no"
              onChange={(event) => props.handleInputChange("location.possession_no", event.target.value)}
              value={locationData.possession_no || ""}
            />
          )}
        </FormField>
        <FormField
          helpText="Calculated from the possession's planned start."
          id="location--week_no"
          label="Week number"
        >
          {(fieldProps) => <TextInput {...fieldProps} disabled name="week_no" value={locationData.week_no || ""} />}
        </FormField>
      </div>

      <SegmentedControl
        id="location--conversion_unit"
        label="Distance measurement"
        name="conversion-unit"
        onChange={(value) => props.handleInputChange("conversion_unit", value)}
        options={[
          { label: "Miles / yards", value: "milesYards" },
          { label: "Miles / chains", value: "milesChains" },
          { label: "Km / metres", value: "kilometersMeters" },
        ]}
        value={props.values?.conversion_unit || "milesYards"}
      />

      <Toggle
        checked={locationData.is_multi_possession === 1}
        description="Use separate planned dates for each possession part."
        id="location--is_multi_possession"
        label="Multiple part possession"
        onChange={(event) => props.handleInputChange("location.is_multi_possession", event.target.checked ? 1 : 0)}
      />
    </section>
  );
};

export default Location;
